import { useQuery } from '@tanstack/react-query';
import { ReactNode, createContext, useContext } from 'react';
import { DEFAULT_LANGUAGE, SupportedLanguages } from '../constants/language';

import i18n, { useI18nService } from '../services/i18n/i18n.service';
import { ApiResult } from '../services/general.types';
import { TranslationData } from '../services/i18n/types';
import LoadingFallback from '../LoadingFallback';

interface I18nContextType {
  changeLanguage: (lang: SupportedLanguages) => Promise<void>;
  currentLanguage: SupportedLanguages;
  error: Error | null;
  isLoading: boolean;
}

interface I18nContextProps {
  children: ReactNode;
}

const I18nContext = createContext<I18nContextType>({} as I18nContextType);

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};

export const I18nProvider = ({ children }: I18nContextProps) => {
  const i18nService = useI18nService();

  const { error, isLoading, isFetching } = useQuery<ApiResult<TranslationData> | null, Error>({
    queryKey: ['translations', i18nService.getCurrentLanguage()],
    queryFn: () => i18nService.initializeI18n(i18nService.getCurrentLanguage()),
    staleTime: 1000 * 60 * 60
  });

  const handleLanguageChange = async (lang: SupportedLanguages) => {
    await i18nService.changeLanguage(lang);
  };


  if (error) {
    console.error('Failed to load translations:', error);
  }

  const value = {
    changeLanguage: handleLanguageChange,
    currentLanguage: i18nService.getCurrentLanguage(),
    error,
    isLoading: isLoading || isFetching,
  };

  if (!i18n.isInitialized) {
    return <LoadingFallback />;
  }

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export default I18nContext;
